/**
 * ConnectionList — one row per connected mailbox (Story MC6 AC5).
 *
 * Row = provider mark (§4 tile) + the mailbox email (Geist Mono) + a StatusDot
 * (design.md §2 hard rule: connected→success · error→danger · revoked→warning) +
 * the short-DE lastSyncAt (`—` when null, never a faked value) + a per-row
 * "Trennen" action (destructive/ghost framing per §2, not Primary blue).
 *
 * Honest empty/loading/error states are the page's (this only renders rows).
 *
 * [Source: apps/web/src/components/ui/StatusDot.tsx; apps/web/src/lib/format.ts]
 */

import { RefreshCw } from 'lucide-react';
import { StatusDot, type StatusTone } from '../../../components/ui/StatusDot';
import { Button } from '../../../components/ui/Button';
import { dateTimeShortDe } from '../../../lib/format';
import { ProviderMark } from './ProviderMark';
import type { MailboxConnectionDto, MailboxConnectionStatus } from '../types';

/** design.md §2 status mapping — the hard rule. */
const STATUS_META: Record<MailboxConnectionStatus, { tone: StatusTone; label: string }> = {
  connected: { tone: 'success', label: 'Verbunden' },
  error: { tone: 'danger', label: 'Fehler' },
  revoked: { tone: 'warning', label: 'Getrennt (neu verbinden)' },
};

export function ConnectionList({
  connections,
  onDisconnect,
  onSyncNow,
  syncingIds,
}: {
  connections: MailboxConnectionDto[];
  onDisconnect: (connection: MailboxConnectionDto) => void;
  /** P2 hardening 06: run one manual sync round for a row (omitted → no sync button). */
  onSyncNow?: (connection: MailboxConnectionDto) => void;
  /** The SET of ids currently syncing — each spins + disables its own button (independent rows). */
  syncingIds?: ReadonlySet<string>;
}) {
  return (
    <ul className="space-y-2">
      {connections.map((c) => {
        const meta = STATUS_META[c.status];
        return (
          <li
            key={c.id}
            className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card px-4 py-3"
          >
            <div className="flex min-w-0 items-center gap-3">
              <ProviderMark provider={c.provider} />
              <div className="min-w-0">
                <div className="truncate font-mono text-sm text-foreground">{c.email}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  <StatusDot tone={meta.tone} label={meta.label} />
                  <span className="text-xs text-muted-foreground">
                    Zuletzt synchronisiert: {c.lastSyncAt ? dateTimeShortDe(c.lastSyncAt) : '-'}
                  </span>
                </div>
                {c.status === 'error' && c.lastError != null && (
                  <div className="mt-0.5 truncate text-xs text-status-danger">{c.lastError}</div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {/* P2 hardening 06 — "Jetzt synchronisieren" per row (hidden for a revoked row). */}
              {onSyncNow && c.status !== 'revoked' && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={syncingIds?.has(c.id) ?? false}
                  onClick={() => onSyncNow(c)}
                  title="Jetzt synchronisieren"
                >
                  <RefreshCw className={`size-4${syncingIds?.has(c.id) ? ' animate-spin' : ''}`} />
                  Synchronisieren
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="text-status-danger hover:bg-status-danger-bg"
                onClick={() => onDisconnect(c)}
              >
                Trennen
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
